/**
 * 발주서 탭 — 목록 + 작성/수정/삭제 + 미리보기 진입.
 *
 * 컬럼: 발주일자, 발주번호, 담당, 수신(매입처), 프로젝트명, 상세내용(첫행+'외'),
 *       공급가총계(VAT별도), 매입연동(Phase C), Excel, 📋(이 내용으로 새 발주서 · v2.213.0)/✏/🗑
 */

import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../lib/auth-context';
import {
  createPo,
  deletePo,
  getPo,
  listPos,
  poDetailToDraft,
  poPdfDownloadUrl,
  updatePo,
  type PoCompany,
  type PoCreateInput,
  type PoDetail,
  type PoDraft,
  type PoListRow,
} from '../../lib/po-api';
import { PurchaseOrderFormModal } from './PurchaseOrderFormModal';
import { PurchaseOrderPreviewModal } from './PurchaseOrderPreviewModal';
import { LinkPurchaseFromPoModal } from './LinkPurchaseFromPoModal';
import { PoPaymentTermModal } from './PoPaymentTermModal';
import { uploadAttachment } from '../../lib/attachment-api';
import { openServerPdfPreview } from '../../lib/pdf-preview';
import { mailSubject } from '../../lib/mail-subject';
import { LoadingLines } from '../../components/ui/Skeleton';

export function PurchaseOrdersTab() {
  const { user } = useAuth();
  const [rows, setRows] = useState<PoListRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [filterCompany, setFilterCompany] = useState<'' | PoCompany>('');
  // 📁 옛 발주서(원본 PDF · v2.291.0) 따로 보기 — '' 전체 · portal 포털에서 쓴 것 · legacy 옛 발주서
  const [filterKind, setFilterKind] = useState<'' | 'portal' | 'legacy'>('');
  const [query, setQuery] = useState('');

  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [formInitial, setFormInitial] = useState<PoDetail | null>(null);
  // create 모드 미리채움 — 기존 발주서 복사(「이 내용으로 새 발주서」 · v2.213.0)
  const [prefillDraft, setPrefillDraft] = useState<PoDraft | null>(null);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const [previewOrder, setPreviewOrder] = useState<PoDetail | null>(null);

  const [linkOpen, setLinkOpen] = useState(false);
  const [linkOrder, setLinkOrder] = useState<PoDetail | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      setRows(await listPos());
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const canModify = (row: PoListRow): boolean => {
    if (!user) return false;
    // 2026-05-29: admin/mgmt/sales 모두 타인 작성건 수정·삭제 가능 (backend 와 동일)
    if (user.role === 'admin' || user.role === 'mgmt' || user.role === 'sales') return true;
    if (user.userId === row.createdBy) return true;
    return false;
  };

  const filtered = rows.filter((r) => {
    if (filterCompany && r.companyId !== filterCompany) return false;
    if (filterKind && (r.poType === 'legacy_pdf') !== (filterKind === 'legacy')) return false;
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return (
      r.poNo.toLowerCase().includes(q) ||
      r.recipientSupplier.toLowerCase().includes(q) ||
      r.projectName.toLowerCase().includes(q) ||
      r.empName.toLowerCase().includes(q) ||
      (r.firstItemName ?? '').toLowerCase().includes(q) ||
      // 관련 영업건 여러 개(v2.284.0) — 대표가 아니어도 찾는다
      [r.linkedSalesId ?? '', ...(r.linkedSalesIds ?? [])].some((x) => x.toLowerCase().includes(q))
    );
  });

  async function handleCreateClick() {
    setFormMode('create');
    setFormInitial(null);
    setPrefillDraft(null);
    setFormOpen(true);
  }

  /**
   * 기존 발주서 내용으로 새 발주서 작성 (v2.213.0 · 견적서 복사와 같은 개념).
   * 원본은 건드리지 않고, 같은 내용을 채운 '작성' 폼을 연다 → 저장 시 새 발주번호로 발번.
   * 무엇을 가져오고 무엇을 비우는지는 po-api 의 poDetailToDraft 가 정한다(영업건·발주일자·첨부 파일은 안 가져옴).
   */
  async function handleCopyClick(poId: number) {
    try {
      const detail = await getPo(poId);
      setFormMode('create');
      setFormInitial(null);
      setPrefillDraft(poDetailToDraft(detail));
      setPreviewOpen(false);
      setFormOpen(true);
    } catch (e) {
      alert('발주서 조회 실패: ' + (e as Error).message);
    }
  }

  async function handleEditClick(row: PoListRow) {
    try {
      const detail = await getPo(row.id);
      setFormMode('edit');
      setFormInitial(detail);
      setPrefillDraft(null);
      setFormOpen(true);
    } catch (e) {
      alert('발주서 조회 실패: ' + (e as Error).message);
    }
  }

  async function handleDeleteClick(row: PoListRow) {
    if (!confirm(`발주서 [${row.poNo}] 를 삭제하시겠습니까?\n( ${row.projectName || '제목 없음'} )`)) {
      return;
    }
    try {
      await deletePo(row.id);
      await reload();
    } catch (e) {
      alert('삭제 실패: ' + (e as Error).message);
    }
  }

  async function handlePreviewClick(row: PoListRow) {
    try {
      const detail = await getPo(row.id);
      setPreviewOrder(detail);
      setPreviewOpen(true);
    } catch (e) {
      alert('미리보기 실패: ' + (e as Error).message);
    }
  }

  async function handleLinkClick(row: PoListRow) {
    try {
      const detail = await getPo(row.id);
      setLinkOrder(detail);
      setLinkOpen(true);
    } catch (e) {
      alert('발주서 조회 실패: ' + (e as Error).message);
    }
  }

  async function handleFormSubmit(data: PoCreateInput, pendingAttachments: File[] = []) {
    if (formMode === 'create') {
      const res = await createPo(data);
      // 견적서 첨부형 신규 작성 — 생성된 발주서 id 로 대기 파일 업로드
      for (const f of pendingAttachments) {
        try {
          await uploadAttachment('po', String(res.id), '견적서', f);
        } catch (e) {
          alert('견적서 첨부 업로드 실패: ' + (e as Error).message);
        }
      }
      setFormOpen(false);
      setPrefillDraft(null);
      await reload();
      const detail = await getPo(res.id);
      setPreviewOrder(detail);
      setPreviewOpen(true);
    } else if (formMode === 'edit' && formInitial) {
      await updatePo(formInitial.id, data);
      setFormOpen(false);
      await reload();
      const detail = await getPo(formInitial.id);
      setPreviewOrder(detail);
      setPreviewOpen(true);
    }
  }

  const canCreate = user?.role === 'admin' || user?.role === 'mgmt' || user?.role === 'sales';

  return (
    <div className="bg-white rounded shadow-sm border border-slate-200 p-4 md:p-5">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <h2 className="ui-section-title mr-auto">발주서 목록 ({filtered.length}건)</h2>
        <select
          value={filterCompany}
          onChange={(e) => setFilterCompany(e.target.value as '' | PoCompany)}
          className="ui-input"
        >
          <option value="">전체 사업자</option>
          <option value="SM">SM</option>
          <option value="EJ">EJ</option>
        </select>
        <select
          value={filterKind}
          onChange={(e) => setFilterKind(e.target.value as '' | 'portal' | 'legacy')}
          className="ui-input"
          title="옛 발주서 = 포털을 쓰기 전에 보낸 발주서 PDF 를 그대로 올린 것"
        >
          <option value="">전체 발주서</option>
          <option value="portal">포털에서 쓴 발주서</option>
          <option value="legacy">📁 옛 발주서(원본 PDF)</option>
        </select>
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="번호/매입처/프로젝트명/담당/품목/영업건 검색"
          className="ui-input w-72"
        />
        {canCreate && (
          <button
            type="button"
            onClick={handleCreateClick}
            className="btn btn-primary"
          >
            + 발주서 작성
          </button>
        )}
        {/* 매입처별 결제조건 — 여기 등록해 두면 발주서 작성에서 자동으로 채워진다 (2026-07-29) */}
        <button
          type="button"
          onClick={() => setTermsOpen(true)}
          title="매입처별 결제조건을 등록해 두면 발주서 작성 시 자동으로 채워집니다"
          className="btn btn-secondary"
        >
          💳 발주처 결제조건
        </button>
      </div>

      {err && (
        <div className="mb-3 px-3 py-2 bg-rose-50 border border-rose-200 text-rose-800 rounded text-sm">
          {err}
        </div>
      )}

      <div className="overflow-x-auto border border-slate-200 rounded">
        <table className="w-full text-[13px]">
          <thead className="bg-slate-50 sticky top-0 z-10">
            <tr>
              <Th>발주일자</Th>
              <Th>사업자</Th>
              <Th>발주번호</Th>
              <Th>담당</Th>
              <Th>수신 (매입처)</Th>
              <Th>프로젝트명</Th>
              <Th>상세내용</Th>
              <Th align="right">공급가 총계<br />(VAT별도)</Th>
              <Th align="center">관련 영업건</Th>
              <Th align="center">매입 연동</Th>
              <Th align="center">PDF</Th>
              <Th align="center">작업</Th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={12} className="text-center py-6 text-slate-400"><LoadingLines /></td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={12} className="text-center py-6 text-slate-400">발주서 없음</td>
              </tr>
            ) : (
              filtered.map((r) => (
                <tr
                  key={r.id}
                  className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                  onClick={() => handlePreviewClick(r)}
                >
                  <Td>{r.poDate}</Td>
                  <Td>
                    <span
                      className={
                        'inline-block px-1.5 py-0.5 text-[11px] rounded font-medium ' +
                        (r.companyId === 'SM'
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-purple-100 text-purple-700')
                      }
                    >
                      {r.companyId}
                    </span>
                  </Td>
                  {/* 옛 발주서 번호(SMSYS-2023-0403-01(2) · 21자)는 앞 「SMSYS-」·「EJIN-」을 떼고 「-」 뒤마다 줄바꿈 자리(wbr) —
                      「-」 다음이 숫자면 브라우저가 줄을 안 바꿔(2026-0120-02(2) 가 한 덩어리) 1280 에서 표가 12px 넘쳤다(실측) */}
                  <Td className="font-mono text-xs" title={r.poNo}>
                    {r.poType === 'legacy_pdf'
                      ? r.poNo.replace(/^(SMSYS|EJIN)-/, '').split('-').map((part, i, all) => (
                          <span key={i}>{part}{i < all.length - 1 && <>-<wbr /></>}</span>
                        ))
                      : r.poNo}
                  </Td>
                  <Td>{r.empDisplay}</Td>
                  <Td>{r.recipientSupplier}</Td>
                  <Td className="max-w-[20ch] truncate" title={r.projectName}>{r.projectName}</Td>
                  <Td className="max-w-[24ch] truncate" title={r.firstItemDescription || r.firstItemName}>
                    {r.poType === 'legacy_pdf' ? (
                      <span className="text-xs text-amber-700 whitespace-nowrap" title="포털을 쓰기 전에 보낸 발주서 PDF 를 그대로 올린 것 — 원본 그대로 보관">📁 옛 발주서(원본 PDF)</span>
                    ) : r.poType === 'quote_attached' ? (
                      <span className="text-xs text-indigo-600 whitespace-nowrap">📎 견적서 첨부형</span>
                    ) : r.firstItemName ? (
                      <>
                        {r.firstItemName}
                        {r.itemCount > 1 ? <span className="text-slate-500"> 외</span> : null}
                      </>
                    ) : (
                      <span className="text-slate-400">-</span>
                    )}
                  </Td>
                  <Td align="right" className="font-medium">{r.totalSupply.toLocaleString()}</Td>
                  <Td align="center" className="font-mono text-xs">
                    {/* 관련 영업건 여러 개(v2.284.0) — 대표 + 「외 N」 · 마우스를 올리면 전부 */}
                    {(r.linkedSalesIds?.length ?? 0) > 1 ? (
                      <span title={r.linkedSalesIds.join('\n')}>
                        {r.linkedSalesIds[0]}<span className="ml-1 font-sans text-slate-500">외 {r.linkedSalesIds.length - 1}</span>
                      </span>
                    ) : (r.linkedSalesId ?? <span className="text-slate-300">-</span>)}
                  </Td>
                  <Td align="center" onClick={(e) => e.stopPropagation()}>
                    {canModify(r) && r.poType === 'standard' ? (
                      <button
                        type="button"
                        onClick={() => handleLinkClick(r)}
                        title={r.linkedPurchaseCount > 0
                          ? `이미 매입 연동함(매입 ${r.linkedPurchaseCount}줄) — 다시 누르면 확인 뒤 매입이 또 생깁니다`
                          : '매입 N건 자동 등록'}
                        className={r.linkedPurchaseCount > 0 ? 'btn btn-success btn-sm' : 'btn btn-outline btn-sm'}
                      >
                        {r.linkedPurchaseCount > 0 ? '🔗✓' : '🔗'}
                      </button>
                    ) : (
                      <span className="text-[11px] text-slate-500">-</span>
                    )}
                  </Td>
                  <Td align="center" onClick={(e) => e.stopPropagation()}>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        openServerPdfPreview({
                          pdfUrl: poPdfDownloadUrl(r.id),
                          filename: `발주서_${r.poNo}.pdf`,
                          // 옛 발주서는 이미 보낸 원본이라 ✉(다시 보내기)를 달지 않는다
                          mailDoc: r.poType === 'legacy_pdf' ? undefined : {
                            docRefs: [{ docType: 'po', docId: r.id, label: `발주서 ${r.poNo}` }],
                            subject: mailSubject('발주서 송부', r.poNo),
                          },
                        });
                      }}
                      className="btn btn-danger btn-sm"
                      title="발주서 미리보기"
                    >
                      📄
                    </button>
                  </Td>
                  {/* 📋 = 이 내용으로 새 발주서 (v2.213.0) · 버튼 크기는 견적서 목록과 같다(px-1·mr-0.5).
                      1300px 이상에서는 3개를 한 줄로(1366 에서도 표 가로 스크롤 0 — 실측) · 1280 에서는 접히게 둔다:
                      묶으면 이 칸이 87px 로 벌어져 표가 50px 넘친다(원래 10px) — 맨 끝 🗑 가 가려진다 (09-28 실측). */}
                  <Td align="center" className="min-[1300px]:whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    {canCreate && r.poType !== 'legacy_pdf' && (
                      <button
                        type="button"
                        onClick={() => handleCopyClick(r.id)}
                        className="btn btn-outline btn-sm mr-0.5"
                        title="이 발주서 내용으로 새 발주서 작성 (원본은 그대로 남습니다)"
                      >
                        📋
                      </button>
                    )}
                    {canModify(r) ? (
                      <>
                        {/* 옛 발주서(원본 PDF)는 보낸 그대로 보관 — 고치기 없음(서버도 400) */}
                        {r.poType !== 'legacy_pdf' && (
                          <button
                            type="button"
                            onClick={() => handleEditClick(r)}
                            className="btn btn-warning btn-sm mr-0.5"
                            title="수정"
                          >
                            ✏
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => handleDeleteClick(r)}
                          className="btn btn-danger btn-sm"
                          title="삭제"
                        >
                          🗑
                        </button>
                      </>
                    ) : (
                      <span className="text-[11px] text-slate-500">조회만</span>
                    )}
                  </Td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {termsOpen && <PoPaymentTermModal canEdit={canCreate} onClose={() => setTermsOpen(false)} />}
      <PurchaseOrderFormModal
        open={formOpen}
        mode={formMode}
        initial={formInitial}
        prefill={formMode === 'create' ? prefillDraft : null}
        onClose={() => {
          setFormOpen(false);
          setPrefillDraft(null);
        }}
        onSubmit={handleFormSubmit}
      />
      <PurchaseOrderPreviewModal
        open={previewOpen}
        order={previewOrder}
        onClose={() => {
          setPreviewOpen(false);
          setPreviewOrder(null);
        }}
        onEdit={
          canCreate && previewOrder && previewOrder.poType !== 'legacy_pdf'
            ? () => {
                setFormMode('edit');
                setFormInitial(previewOrder);
                setPrefillDraft(null);
                setPreviewOpen(false);
                setFormOpen(true);
              }
            : undefined
        }
        onCopy={canCreate && previewOrder && previewOrder.poType !== 'legacy_pdf' ? () => handleCopyClick(previewOrder.id) : undefined}
      />
      <LinkPurchaseFromPoModal
        open={linkOpen}
        order={linkOrder}
        onClose={() => {
          setLinkOpen(false);
          setLinkOrder(null);
        }}
      />
    </div>
  );
}

function Th({
  children,
  align,
}: {
  children: React.ReactNode;
  align?: 'left' | 'right' | 'center';
}) {
  const cls = align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left';
  return (
    <th className={`px-2 py-1.5 text-xs font-medium text-slate-600 whitespace-nowrap ${cls}`}>
      {children}
    </th>
  );
}

function Td({
  children,
  align,
  className,
  title,
  onClick,
}: {
  children: React.ReactNode;
  align?: 'left' | 'right' | 'center';
  className?: string;
  title?: string;
  onClick?: (e: React.MouseEvent) => void;
}) {
  const cls = align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left';
  return (
    <td
      className={`px-2 py-1.5 text-sm text-slate-700 align-middle ${cls} ${className ?? ''}`}
      title={title}
      onClick={onClick}
    >
      {children}
    </td>
  );
}
